import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Badge,
  Button,
  Card,
  CardBody,
  Col,
  Container,
  Input,
  Row,
} from "reactstrap";

import { Paginator } from "../../components";
import { BreadCrumb } from "../../components/Common";
import { PaginateQuery } from "../../interfaces";
import { RequireSuperAdmin } from "../../routes/AuthProtected";
import {
  TesterFeedback,
  TesterUser,
  useAddTesterMutation,
  useGetTesterFeedbackFacetsQuery,
  useGetTesterFeedbackQuery,
  useGetTestersQuery,
  useLazySearchTesterUsersQuery,
  useRemoveTesterMutation,
  useSetTesterFeedbackFixedMutation,
} from "../../store/api/testerApi";

const displayName = (user: TesterUser, arabic: boolean) =>
  arabic ? user.name?.ar || user.name?.en : user.name?.en || user.name?.ar;

const TesterRow = ({
  user,
  arabic,
  action,
  className,
}: {
  user: TesterUser;
  arabic: boolean;
  action: React.ReactNode;
  className?: string;
}) => {
  const { t } = useTranslation();
  return (
    <div
      className={`d-flex justify-content-between align-items-center gap-3 py-2 ${
        className || ""
      }`}
    >
      <div className="min-w-0">
        <div className="fw-medium text-truncate">{displayName(user, arabic)}</div>
        <div className="text-muted small">
          {user.phone || ""}
          {user.phone && user.role ? " · " : ""}
          {user.role ? t(user.role) : ""}
        </div>
      </div>
      {action}
    </div>
  );
};

const deviceSummary = (item: TesterFeedback, t: (key: string) => string) => {
  const bits = [
    item.platform
      ? item.platform === "ios"
        ? t("ios")
        : item.platform === "android"
          ? t("android")
          : item.platform
      : null,
    item.osVersion
      ? `${item.osName || item.platform || "OS"} ${item.osVersion}`
      : null,
    item.appVersion
      ? `v${item.appVersion}${item.appBuild ? ` (${t("build")} ${item.appBuild})` : ""}`
      : null,
    item.deviceModel,
    item.isTablet === true
      ? t("tablet")
      : item.isTablet === false
        ? t("phone")
        : null,
    item.isEmulator ? t("emulator") : null,
  ].filter(Boolean);
  return bits.join(" · ");
};

const TestersPage = () => {
  const { t, i18n } = useTranslation();
  const arabic = i18n.language === "ar";
  const [paginateQuery, setPaginateQuery] = useState<PaginateQuery>(
    new PaginateQuery(10, "created_at:ASC"),
  );
  const [feedbackQuery, setFeedbackQuery] = useState<PaginateQuery>(() => {
    const query = new PaginateQuery(10, "created_at:DESC");
    query.filter = {};
    return query;
  });
  const [feedbackSearch, setFeedbackSearch] = useState("");
  const { data: paginatedTesters } = useGetTestersQuery(paginateQuery);
  const testers = paginatedTesters?.data || [];
  const { data: feedbackPage } = useGetTesterFeedbackQuery(feedbackQuery);
  const feedback = feedbackPage?.data || [];
  const { data: facets } = useGetTesterFeedbackFacetsQuery();
  const [setFeedbackFixed, { isLoading: isUpdatingFixed }] =
    useSetTesterFeedbackFixedMutation();
  const [searchTesterUsers, { isFetching }] = useLazySearchTesterUsersQuery();
  const [addTester, { isLoading: isAdding }] = useAddTesterMutation();
  const [removeTester] = useRemoveTesterMutation();
  const [search, setSearch] = useState("");
  const [matches, setMatches] = useState<TesterUser[]>([]);
  const [hasSearched, setHasSearched] = useState(false);
  const testerIds = new Set(testers.map((tester) => tester.id));
  const matchIds = new Set(matches.map((match) => match.id));
  const testersNotInResults = hasSearched
    ? testers.filter((tester) => !matchIds.has(tester.id))
    : testers;

  const filterValue = useMemo(
    () => ({
      platform: String(feedbackQuery.filter?.platform || ""),
      appVersion: String(feedbackQuery.filter?.appVersion || ""),
      osVersion: String(feedbackQuery.filter?.osVersion || ""),
      isFixed: String(feedbackQuery.filter?.isFixed || ""),
    }),
    [feedbackQuery.filter],
  );

  const markMatch = (userId: string, isTester: boolean) => {
    setMatches((current) =>
      current.map((match) =>
        match.id === userId ? { ...match, isTester } : match,
      ),
    );
  };

  const removeButton = (userId: string) => (
    <Button
      color="link"
      className="text-danger text-decoration-none"
      type="button"
      onClick={() => onRemove(userId)}
    >
      {t("remove")}
    </Button>
  );

  const onSearch = async () => {
    if (search.trim().length < 2) {
      setMatches([]);
      setHasSearched(false);
      return;
    }
    const found = await searchTesterUsers(search.trim()).unwrap();
    setMatches(found);
    setHasSearched(true);
  };

  const onAdd = async (userId: string) => {
    await addTester(userId).unwrap();
    markMatch(userId, true);
  };

  const onRemove = async (userId: string) => {
    await removeTester(userId).unwrap();
    markMatch(userId, false);
    if (testers.length === 1 && paginateQuery.page > 1) {
      setPaginateQuery({ ...paginateQuery, page: paginateQuery.page - 1 });
    }
  };

  const updateFeedbackFilter = (key: string, value: string) => {
    const nextFilter = { ...(feedbackQuery.filter || {}) };
    if (!value) {
      delete nextFilter[key];
    } else {
      nextFilter[key] = value;
    }
    setFeedbackQuery({
      ...feedbackQuery,
      page: 1,
      filter: nextFilter,
    });
  };

  const onFeedbackSearch = () => {
    setFeedbackQuery({
      ...feedbackQuery,
      page: 1,
      search: feedbackSearch.trim(),
    });
  };

  return (
    <RequireSuperAdmin>
    <div className="page-content">
      <Container fluid>
        <BreadCrumb title={t("testers")} pageTitle={t("users")} />
        <Row>
          <Col lg={5}>
            <Card>
              <CardBody>
                <p className="text-muted">{t("testersHint")}</p>
                <div className="d-flex gap-2 mb-3">
                  <Input
                    value={search}
                    placeholder={t("nameOrPhone")}
                    onChange={(event) => setSearch(event.target.value)}
                    onKeyDown={(event) => {
                      if (event.key === "Enter") {
                        event.preventDefault();
                        onSearch();
                      }
                    }}
                  />
                  <Button
                    color="primary"
                    type="button"
                    disabled={isFetching}
                    onClick={onSearch}
                  >
                    {t("search")}
                  </Button>
                </div>
                {hasSearched ? (
                  <div className="border rounded mb-4">
                    <div className="px-3 py-2 border-bottom bg-light small text-muted">
                      {t("testerSearchResults")}
                    </div>
                    {matches.length === 0 ? (
                      <div className="px-3 py-3 text-muted">
                        {t("noTesterSearchResults")}
                      </div>
                    ) : (
                      matches.map((match, index) => (
                        <div
                          key={match.id}
                          className={
                            index < matches.length - 1
                              ? "border-bottom"
                              : undefined
                          }
                        >
                          <TesterRow
                            user={match}
                            arabic={arabic}
                            className="px-3"
                            action={
                              match.isTester || testerIds.has(match.id) ? (
                                removeButton(match.id)
                              ) : (
                                <Button
                                  color="primary"
                                  size="sm"
                                  type="button"
                                  disabled={isAdding}
                                  onClick={() => onAdd(match.id)}
                                >
                                  {t("add")}
                                </Button>
                              )
                            }
                          />
                        </div>
                      ))
                    )}
                  </div>
                ) : null}
                <h6 className="mb-2">{t("testers")}</h6>
                {testersNotInResults.map((tester) => (
                  <div key={tester.id} className="border-top">
                    <TesterRow
                      user={tester}
                      arabic={arabic}
                      action={removeButton(tester.id)}
                    />
                  </div>
                ))}
                {(paginatedTesters?.meta?.totalItems || 0) === 0 ? (
                  <div className="text-muted">{t("noTestersYet")}</div>
                ) : null}
                {testers.length > 0 ? (
                  <Paginator
                    itemsOnCurrentPage={testers.length}
                    itemsPerPage={
                      paginatedTesters?.meta?.itemsPerPage as number
                    }
                    totalItems={paginatedTesters?.meta?.totalItems || 0}
                    currentPage={paginatedTesters?.meta?.currentPage || 1}
                    totalPages={paginatedTesters?.meta?.totalPages || 0}
                    onChangePage={(page) =>
                      setPaginateQuery({ ...paginateQuery, page })
                    }
                  />
                ) : null}
              </CardBody>
            </Card>
          </Col>
          <Col lg={7}>
            <Card>
              <CardBody>
                <h5 className="mb-3">{t("testerFeedback")}</h5>
                <Row className="g-2 mb-3">
                  <Col md={6}>
                    <Input
                      value={feedbackSearch}
                      placeholder={t("feedbackSearchPlaceholder")}
                      onChange={(event) => setFeedbackSearch(event.target.value)}
                      onKeyDown={(event) => {
                        if (event.key === "Enter") {
                          event.preventDefault();
                          onFeedbackSearch();
                        }
                      }}
                    />
                  </Col>
                  <Col md={2}>
                    <Button
                      color="primary"
                      className="w-100"
                      type="button"
                      onClick={onFeedbackSearch}
                    >
                      {t("search")}
                    </Button>
                  </Col>
                  <Col md={4}>
                    <Input
                      type="select"
                      value={filterValue.isFixed}
                      onChange={(event) =>
                        updateFeedbackFilter("isFixed", event.target.value)
                      }
                    >
                      <option value="">{t("feedbackFixedStatus")}: {t("feedbackAll")}</option>
                      <option value="false">{t("feedbackOpen")}</option>
                      <option value="true">{t("feedbackFixed")}</option>
                    </Input>
                  </Col>
                  <Col md={4}>
                    <Input
                      type="select"
                      value={filterValue.platform}
                      onChange={(event) =>
                        updateFeedbackFilter("platform", event.target.value)
                      }
                    >
                      <option value="">{t("feedbackPlatform")}: {t("feedbackAll")}</option>
                      {(facets?.platforms?.length
                        ? facets.platforms
                        : ["ios", "android"]
                      ).map((platform) => (
                        <option key={platform} value={platform}>
                          {platform === "ios"
                            ? t("ios")
                            : platform === "android"
                              ? t("android")
                              : platform}
                        </option>
                      ))}
                    </Input>
                  </Col>
                  <Col md={4}>
                    <Input
                      type="select"
                      value={filterValue.appVersion}
                      onChange={(event) =>
                        updateFeedbackFilter("appVersion", event.target.value)
                      }
                    >
                      <option value="">
                        {t("feedbackAppVersion")}: {t("feedbackAll")}
                      </option>
                      {(facets?.appVersions || []).map((version) => (
                        <option key={version} value={version}>
                          {version}
                        </option>
                      ))}
                    </Input>
                  </Col>
                  <Col md={4}>
                    <Input
                      type="select"
                      value={filterValue.osVersion}
                      onChange={(event) =>
                        updateFeedbackFilter("osVersion", event.target.value)
                      }
                    >
                      <option value="">
                        {t("feedbackOsVersion")}: {t("feedbackAll")}
                      </option>
                      {(facets?.osVersions || []).map((version) => (
                        <option key={version} value={version}>
                          {version}
                        </option>
                      ))}
                    </Input>
                  </Col>
                </Row>

                {feedback.map((item) => (
                  <div key={item.id} className="border-top py-3">
                    <div className="d-flex justify-content-between align-items-start gap-2">
                      <div className="min-w-0">
                        <div className="fw-semibold">
                          {item.user ? displayName(item.user, arabic) : ""}
                          {item.user?.phone ? ` (${item.user.phone})` : ""}
                        </div>
                        <div className="mt-1">{item.message || "—"}</div>
                        <div className="text-muted small mt-1">
                          {deviceSummary(item, t) || t("feedbackDeviceDetails")}
                        </div>
                        {(item.locale ||
                          item.timezone ||
                          item.orientation ||
                          item.screenWidth) && (
                          <div className="text-muted small">
                            {[
                              item.locale,
                              item.timezone,
                              item.orientation,
                              item.screenWidth && item.screenHeight
                                ? `${item.screenWidth}×${item.screenHeight}`
                                : null,
                            ]
                              .filter(Boolean)
                              .join(" · ")}
                          </div>
                        )}
                      </div>
                      <div className="d-flex flex-column align-items-end gap-2">
                        <Badge color={item.isFixed ? "success" : "warning"}>
                          {item.isFixed ? t("feedbackFixed") : t("feedbackOpen")}
                        </Badge>
                        <Button
                          color={item.isFixed ? "secondary" : "success"}
                          size="sm"
                          type="button"
                          disabled={isUpdatingFixed}
                          onClick={() =>
                            setFeedbackFixed({
                              id: item.id,
                              isFixed: !item.isFixed,
                            })
                          }
                        >
                          {item.isFixed
                            ? t("reopenFeedback")
                            : t("markFeedbackFixed")}
                        </Button>
                      </div>
                    </div>
                    {item.media?.length ? (
                      <div className="d-flex flex-wrap gap-2 mt-2">
                        {item.media.map((file) =>
                          file.type === "video" ? (
                            <video
                              key={file.id}
                              src={file.videoUrl || file.path}
                              poster={file.thumbnail}
                              controls
                              style={{ maxWidth: 240, maxHeight: 160 }}
                            />
                          ) : (
                            <a
                              key={file.id}
                              href={file.path}
                              target="_blank"
                              rel="noreferrer"
                            >
                              <img
                                src={file.thumbnail || file.path}
                                alt={file.name || ""}
                                style={{
                                  maxWidth: 160,
                                  maxHeight: 120,
                                  objectFit: "cover",
                                }}
                              />
                            </a>
                          ),
                        )}
                      </div>
                    ) : null}
                    <div className="text-muted small mt-2">
                      {item.createdAt
                        ? new Date(item.createdAt).toLocaleString(
                            arabic ? "ar" : "en",
                          )
                        : ""}
                      {item.isFixed && item.fixedAt
                        ? ` · ${t("feedbackFixed")}: ${new Date(
                            item.fixedAt,
                          ).toLocaleString(arabic ? "ar" : "en")}`
                        : ""}
                    </div>
                  </div>
                ))}
                {feedback.length === 0 ? (
                  <div className="text-muted">{t("noTesterFeedbackYet")}</div>
                ) : null}
                {(feedbackPage?.meta?.totalItems || 0) > 0 ? (
                  <Paginator
                    itemsOnCurrentPage={feedback.length}
                    itemsPerPage={feedbackPage?.meta?.itemsPerPage as number}
                    totalItems={feedbackPage?.meta?.totalItems || 0}
                    currentPage={feedbackPage?.meta?.currentPage || 1}
                    totalPages={feedbackPage?.meta?.totalPages || 0}
                    onChangePage={(page) =>
                      setFeedbackQuery({ ...feedbackQuery, page })
                    }
                  />
                ) : null}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
    </RequireSuperAdmin>
  );
};

export default TestersPage;
